import type { FormEvent } from 'react';
import { useCallback, useId, useState } from 'react';
import { signInWithPhoneNumber, type ConfirmationResult } from 'firebase/auth';

import { REGEXP_ONLY_DIGITS } from 'input-otp';

import { Button } from '@/shared/ui/shadcn/button';
import { FieldError, FieldLabel } from '@/shared/ui/shadcn/field';
import { LotusLoader } from '@/shared/ui/primitives/lotus-loader';
import { Input } from '@/shared/ui/shadcn/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/shared/ui/shadcn/input-otp';
import { auth, logger } from '@/shared';
import { formatPhoneInput } from '@/shared/lib/formatters';

import { getRecaptchaVerifier, normalizePhoneNumber, resetRecaptchaVerifier } from '../index.js';

type BusyState = 'idle' | 'sending' | 'verifying';

export interface PhoneOtpFormProps {
  title?: string;
  className?: string;
}

export function PhoneOtpForm({ title = 'Sign in', className }: PhoneOtpFormProps) {
  const [busy, setBusy] = useState<BusyState>('idle');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const phoneId = useId();
  const otpId = useId();

  const isSending = busy === 'sending';
  const isVerifying = busy === 'verifying';
  const isBusy = busy !== 'idle';
  const isPhoneComplete = phone.replace(/\D/g, '').length >= 10;

  const onSend = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (isBusy || confirmation) return;

      setErrorMessage(null);
      setBusy('sending');
      try {
        const appVerifier = getRecaptchaVerifier();
        const nextConfirmation = await signInWithPhoneNumber(
          auth,
          normalizePhoneNumber(phone),
          appVerifier
        );
        setConfirmation(nextConfirmation);
        setBusy('idle');
      } catch (error: unknown) {
        resetRecaptchaVerifier();
        setBusy('idle');
        setErrorMessage('Failed to send verification code.');
        logger.error('otp send failed', { source: 'features/auth/phone-otp' }, error);
      }
    },
    [confirmation, isBusy, phone]
  );

  const onVerify = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (isBusy || !confirmation) return;

      setErrorMessage(null);
      setBusy('verifying');
      try {
        await confirmation.confirm(code);
        resetRecaptchaVerifier();
        setCode('');
        setConfirmation(null);
        setBusy('idle');
      } catch (error: unknown) {
        setBusy('idle');
        setErrorMessage('Invalid verification code.');
        logger.error('otp verify failed', { source: 'features/auth/phone-otp' }, error);
      }
    },
    [code, confirmation, isBusy]
  );

  const heading = confirmation ? 'Enter verification code' : title;
  const subtitle = confirmation
    ? 'We just sent a six digit verification code'
    : 'Use your phone number to receive a verification code.';

  return (
    <div className={`flex w-full flex-col gap-[22px] ${className ?? ''}`}>
      <h1 className="text-2xl font-medium">{heading}</h1>
      <p className="text-muted-foreground text-sm">{subtitle}</p>

      {!confirmation ? (
        <form onSubmit={onSend} className="flex w-full flex-col gap-[22px]">
          <FieldLabel htmlFor={phoneId} className="sr-only">
            Phone number
          </FieldLabel>
          <Input
            id={phoneId}
            type="tel"
            inputSize="lg"
            radius="default"
            value={phone}
            onChange={(e) => setPhone(formatPhoneInput(e.currentTarget.value))}
            autoComplete="tel"
            inputMode="tel"
            placeholder="(555) 555-5555"
            aria-invalid={errorMessage ? true : undefined}
            disabled={isBusy}
            required
            className="rounded-none border-x-0 border-t-0 border-b border-b-border/80 px-0 bg-transparent shadow-none focus-visible:border-primary focus-visible:ring-0 dark:border-b-white/40 dark:bg-transparent"
          />
          <FieldError>{errorMessage}</FieldError>

          <div className="relative w-full">
            <Button
              type="submit"
              radius="pill"
              size="lg"
              className="w-full"
              disabled={isBusy || !isPhoneComplete}
            >
              <span className={isSending ? 'opacity-0' : undefined}>Send code</span>
            </Button>
            {isSending && (
              <LotusLoader size={28} className="pointer-events-none absolute inset-0 m-auto" />
            )}
          </div>
        </form>
      ) : (
        <form onSubmit={onVerify} className="flex w-full flex-col gap-[22px]">
          <FieldLabel htmlFor={otpId} className="sr-only">
            Verification code
          </FieldLabel>
          <InputOTP
            id={otpId}
            value={code}
            onChange={setCode}
            maxLength={6}
            pattern={REGEXP_ONLY_DIGITS}
            autoComplete="one-time-code"
            inputMode="numeric"
            containerClassName="justify-start"
            aria-invalid={errorMessage ? true : undefined}
            disabled={isBusy}
            required
            autoFocus
          >
            <InputOTPGroup className="gap-2.5 *:data-[slot=input-otp-slot]:size-12 *:data-[slot=input-otp-slot]:rounded-none *:data-[slot=input-otp-slot]:border-x-0 *:data-[slot=input-otp-slot]:border-t-0 *:data-[slot=input-otp-slot]:border-b *:data-[slot=input-otp-slot]:border-b-border/80 *:data-[slot=input-otp-slot]:bg-transparent *:data-[slot=input-otp-slot]:shadow-none *:data-[slot=input-otp-slot]:data-[active=true]:border-primary *:data-[slot=input-otp-slot]:data-[active=true]:ring-0 dark:*:data-[slot=input-otp-slot]:border-b-white/40 dark:*:data-[slot=input-otp-slot]:bg-transparent">
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
          <FieldError className="text-left">{errorMessage}</FieldError>

          <div className="relative w-full">
            <Button
              type="submit"
              radius="pill"
              size="lg"
              className="w-full"
              disabled={isBusy || code.length !== 6}
            >
              <span className={isVerifying ? 'opacity-0' : undefined}>Verify</span>
            </Button>
            {isVerifying && (
              <LotusLoader size={28} className="pointer-events-none absolute inset-0 m-auto" />
            )}
          </div>
        </form>
      )}
    </div>
  );
}
